import { useLayoutEffect, useState } from 'react';

/**
 * Mantém o scrollElement do virtualizer alinhado a um ref externo.
 * Corrige remount (ex.: sair/voltar do Board): ref stale ou clientHeight 0
 * fazia getVirtualItems() vazio com spacer ainda alto.
 */
export function useVirtualizerScrollElement(scrollElementRef, fallbackRef, deps = []) {
  const [scrollEl, setScrollEl] = useState(null);
  const [viewportVersion, setViewportVersion] = useState(0);

  useLayoutEffect(() => {
    let raf1 = 0;
    let raf2 = 0;
    let ro = null;

    const readEl = () => scrollElementRef?.current ?? fallbackRef?.current ?? null;

    const bump = (el) => {
      setScrollEl((prev) => (prev === el ? prev : el));
      if (el && el.isConnected && el.clientHeight > 0) {
        setViewportVersion((v) => v + 1);
      }
    };

    const sync = () => {
      const el = readEl();
      bump(el);
      return el;
    };

    const el0 = sync();

    // 2 frames: layout do kanban após trocar activeSection
    raf1 = requestAnimationFrame(() => {
      sync();
      raf2 = requestAnimationFrame(() => {
        sync();
      });
    });

    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        const el = readEl();
        bump(el);
      });
      if (el0) ro.observe(el0);
    }

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps explícitas do caller
  }, [scrollElementRef, fallbackRef, ...deps]);

  // Troca de nó: re-observa
  useLayoutEffect(() => {
    if (!scrollEl || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      if (scrollEl.isConnected && scrollEl.clientHeight > 0) {
        setViewportVersion((v) => v + 1);
      }
    });
    ro.observe(scrollEl);
    return () => ro.disconnect();
  }, [scrollEl]);

  return { scrollEl, viewportVersion };
}

export default useVirtualizerScrollElement;
